import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ClientLayout } from "../components/ClientLayout";
import { ClientOrderCard } from "../components/ClientOrderCard";
import { clientApi, type ClientOrder, type ClientProfile } from "../services/clientApi.service";
import { getBusinessNow, getBusinessTodayDate } from "@/utils/businessTime";

/**
 * Calcule la prochaine deadline 10h Cayenne sous forme d'objet humanisé + temps restant en ms.
 * Si on est après 10h aujourd'hui : la deadline est demain 10h.
 */
function useTodayDeadline(): {
  deadlineAt: Date;
  remainingMs: number;
  isPastForToday: boolean;
} {
  const [now, setNow] = useState<Date>(() => getBusinessNow());
  useEffect(() => {
    const id = setInterval(() => setNow(getBusinessNow()), 60_000);
    return () => clearInterval(id);
  }, []);

  const todayBusiness = getBusinessTodayDate();
  const todayDeadline = new Date(todayBusiness);
  todayDeadline.setHours(10, 0, 0, 0);

  const isPastForToday = now.getTime() >= todayDeadline.getTime();
  const deadlineAt = isPastForToday
    ? new Date(todayDeadline.getTime() + 24 * 60 * 60 * 1000)
    : todayDeadline;
  return {
    deadlineAt,
    remainingMs: Math.max(0, deadlineAt.getTime() - now.getTime()),
    isPastForToday,
  };
}

function formatRemaining(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h} h ${m.toString().padStart(2, "0")}`;
  return `${m} min`;
}

export function ClientHomePage(): JSX.Element {
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [todayOrders, setTodayOrders] = useState<ClientOrder[]>([]);
  const [upcomingOrders, setUpcomingOrders] = useState<ClientOrder[]>([]);
  const [hasOrderHistory, setHasOrderHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const todayIso = getBusinessTodayDate().toISOString().slice(0, 10);
        const [me, ordersFromToday, allOrders] = await Promise.all([
          clientApi.getMe(),
          clientApi.listOrders({ deliveryDateFrom: todayIso, limit: 50, page: 1 }),
          clientApi.listOrders({ limit: 1, page: 1 }),
        ]);
        if (cancelled) return;
        setProfile(me);
        setHasOrderHistory((allOrders.total ?? allOrders.data.length) > 0);
        const active = ordersFromToday.data.filter((o) => o.status !== "CANCELLED");
        const today = active
          .filter((o) => o.deliveryDate.slice(0, 10) === todayIso)
          .sort((a, b) => a.orderNumber.localeCompare(b.orderNumber));
        const future = active
          .filter(
            (o) =>
              o.deliveryDate.slice(0, 10) > todayIso && o.status !== "DELIVERED",
          )
          .sort(
            (a, b) =>
              a.deliveryDate.localeCompare(b.deliveryDate) ||
              a.orderNumber.localeCompare(b.orderNumber),
          );
        setTodayOrders(today);
        setUpcomingOrders(future);
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger votre tableau de bord.";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const { deadlineAt, remainingMs, isPastForToday } = useTodayDeadline();
  const deadlineLabel = useMemo(() => {
    const dayLabel = isPastForToday ? "demain" : "aujourd'hui";
    return `Commandez avant ${format(deadlineAt, "HH:mm", { locale: fr })} ${dayLabel}`;
  }, [deadlineAt, isPastForToday]);

  const firstName = profile?.firstName?.trim() || profile?.customer.firstName?.trim();
  const greeting = firstName ? `Bonjour ${firstName}` : "Bonjour";

  /** Prochaines commandes groupées par date de livraison. */
  const upcomingByDate = useMemo(() => {
    const map = new Map<string, ClientOrder[]>();
    for (const order of upcomingOrders) {
      const date = order.deliveryDate.slice(0, 10);
      const list = map.get(date) ?? [];
      list.push(order);
      map.set(date, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [upcomingOrders]);

  return (
    <ClientLayout title="Accueil">
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {greeting}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {format(getBusinessTodayDate(), "EEEE d MMMM yyyy", { locale: fr })}
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {/* Deadline */}
        <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "secondary.main" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <RestaurantMenuRoundedIcon color="secondary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {deadlineLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Plus que {formatRemaining(remainingMs)} pour passer commande
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* CTA principal */}
        <Button
          component={RouterLink}
          to="/client/order/new"
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<RestaurantMenuRoundedIcon />}
        >
          Commander un repas
        </Button>

        {/* Commandes du jour */}
        {loading ? (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          </Paper>
        ) : todayOrders.length > 0 ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {todayOrders.length > 1 ? "Commandes du jour" : "Commande du jour"}
            </Typography>
            <Stack spacing={0} divider={<Divider flexItem />}>
              {todayOrders.map((order) => (
                <ClientOrderCard key={order.id} order={order} />
              ))}
            </Stack>
          </Paper>
        ) : null}

        {/* Prochaines commandes (autres jours) */}
        {!loading && todayOrders.length === 0 && upcomingByDate.length > 0 ? (
          <Stack spacing={2}>
            {upcomingByDate.map(([date, orders]) => (
              <Paper key={date} sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  {orders.length > 1 ? "Prochaines commandes" : "Prochaine commande"} ·{" "}
                  {format(parseISO(date), "EEEE d MMMM", { locale: fr })}
                </Typography>
                <Stack spacing={0} divider={<Divider flexItem />}>
                  {orders.map((order) => (
                    <ClientOrderCard key={order.id} order={order} />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : null}

        {!loading && todayOrders.length === 0 && upcomingByDate.length === 0 ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Commandes à venir
            </Typography>
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                {hasOrderHistory
                  ? "Aucune commande à venir pour le moment."
                  : "Aucune commande à venir."}
              </Typography>
              {hasOrderHistory ? (
                <Button
                  component={RouterLink}
                  to="/client/orders"
                  size="small"
                  variant="text"
                  sx={{ px: 0, minWidth: 0 }}
                >
                  Voir mes commandes passées
                </Button>
              ) : null}
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </ClientLayout>
  );
}
